import { RemoteInterface } from "./remote_interface/RemoteInterface"
import LocalStorage from "./LocalStorage"
import SettingsService from "./SettingsService"
import Account from "models/Account"
import * as FirebaseApp from "firebase/app";
import * as FirebaseAuth from "firebase/auth";
import { DeferredPromise } from 'core-ts/common'

class AccountService
{
    readonly STORAGE_PATH = '/LocalStorage/account.json';

    readonly FIREBASE_CONFIG = {
        apiKey: "AIzaSyA8KxmZKpm1hsTXbyJcq8NKfgt-RA7gmfM",
        authDomain: "dimx-world.firebaseapp.com",
        databaseURL: "https://dimx-world.firebaseio.com",
        projectId: "dimx-world",
        storageBucket: "dimx-world.appspot.com",
        messagingSenderId: "966573284727",
        appId: "1:966573284727:web:adc2890d106fea53d25572",
        measurementId: "G-QLG2XX5571"
    };

    mRemoteInterface: RemoteInterface;

    mFirebaseApp: FirebaseApp.FirebaseApp
    mFirebaseAuth: FirebaseAuth.Auth

    mAccountChangeCallbacks: ((account: Account) => void)[] = []

    mCurrentAccount: Account = new Account(null);

    mCreateAccountInProgress = false

    loginPromise?: DeferredPromise<any>

    constructor() {
        this.mRemoteInterface = new RemoteInterface(this.refreshToken.bind(this));
        //this.mRemoteInterface.setRefreshTokenFunction(this.refreshToken.bind(this));

        const accountData = LocalStorage.get(this.STORAGE_PATH)
        if (accountData) {
            this.mRemoteInterface.setPendingLogin(true);
        }
        this.setAccountData(accountData, true);

        this.mFirebaseApp = FirebaseApp.initializeApp(this.FIREBASE_CONFIG)
        this.mFirebaseAuth = FirebaseAuth.getAuth(this.mFirebaseApp)
        this.mFirebaseAuth.onAuthStateChanged(this.onFirebaseAuthStateChanged.bind(this))
        this.mFirebaseAuth.setPersistence(FirebaseAuth.browserLocalPersistence)
    }

    async refreshToken(): Promise<string> {
        const token = await this.mFirebaseAuth.currentUser?.getIdToken();
        if (!token) {
            return ''; 
        }
        return token;
    }

    subscribeOnAccountChange(callback: (account: Account) => void) {
        this.mAccountChangeCallbacks.push(callback)
    }

    private async onFirebaseAuthStateChanged(user: FirebaseAuth.User | null) {
        console.log(`onFirebaseAuthStateChanged: ${user ? user.uid : 'null'}`)
        if (this.mCreateAccountInProgress) {
            return
        }

        if (!user) {
            this.setAccountData(null);
            return
        }

        try {
            let accountData = await this.mRemoteInterface.login(await user?.getIdToken());
            this.setAccountData(accountData);
            if (this.loginPromise) {
                this.loginPromise.resolve!("OK");
            }
        } catch (error) {
            if (this.loginPromise) {
                this.loginPromise.reject!(error as string);
            }
        }

        this.mRemoteInterface.setPendingLogin(false);
        if (this.loginPromise) {
            delete this.loginPromise;
        }
    }

    private setAccountData(accountData: any, fromLocalStorage?: boolean) {
        if (!fromLocalStorage) {
            LocalStorage.set(this.STORAGE_PATH, accountData);
        }

        const prevAccount = this.mCurrentAccount;
        this.mCurrentAccount = new Account(accountData);

        if (prevAccount?.id && !this.mCurrentAccount?.id) {
            this.mRemoteInterface.logout();
        }

        this.mRemoteInterface.setAccountId(this.mCurrentAccount.id);

        SettingsService.updateSettingsGroups(this.mCurrentAccount)

        // Do not force page refresh when the account didn't actually change
        if (!this.mCurrentAccount.isEqual(prevAccount)) {
            for (const callback of this.mAccountChangeCallbacks) {
                callback(this.mCurrentAccount)
            }
        }
    }

    currentAccount() {
        return this.mCurrentAccount
    }

    async reloadCurrentAccount() {
        if (this.mCurrentAccount?.id) {
            this.setAccountData(await this.mRemoteInterface.request("GET_ACCOUNT", null));
        }
    }

    async createAccount(email: string, password: string)
    {
        // block onFirebaseAuthStateChanged until CREATE_ACCOUNT is finished
        this.mCreateAccountInProgress = true
        console.log(`createAccount: ${email}`);
        try {
            let creadentials = await FirebaseAuth.createUserWithEmailAndPassword(this.mFirebaseAuth, email, password)
            await this.mRemoteInterface.request("CREATE_ACCOUNT",
                { email: email, accountId: creadentials.user.uid});
            this.mCreateAccountInProgress = false
            this.onFirebaseAuthStateChanged(creadentials.user)
        } catch (error) {
            console.error(`Failed to create account: ${error}`)
            this.mCreateAccountInProgress = false
            throw error
        }

        return true;
    }

    async deleteCurrentAccount(password: string)
    {
        if (!this.mFirebaseAuth.currentUser) {
            throw 'not logged in'
        }

        const email = this.mFirebaseAuth.currentUser.email
        if (!email) {
            throw 'invalid user email'
        }

        const emailCreds = FirebaseAuth.EmailAuthProvider.credential(email, password);
        let userCreds = await FirebaseAuth.reauthenticateWithCredential(this.mFirebaseAuth.currentUser, emailCreds)
        
        await this.mRemoteInterface.request("DELETE_ACCOUNT", null);

        return FirebaseAuth.deleteUser(userCreds.user)
    }

    async login(email: string, password: string)
    {
        if(this.loginPromise){
            this.loginPromise.reject!("New login requested");
            delete this.loginPromise;
        }
        await FirebaseAuth.signOut(this.mFirebaseAuth);
        
        this.mRemoteInterface.setPendingLogin(true);
        
        try{
            await FirebaseAuth.signInWithEmailAndPassword(this.mFirebaseAuth, email, password);
        } catch (error) {
            if (error instanceof Error) {
                console.log(`Firebase sign-in failed: ${error.message}`);
            } else {
                console.log(`Unexpected error: ${String(error)}`);
            }
            throw error;
        }

        this.loginPromise = new DeferredPromise<any>();
        return this.loginPromise.promise();
    }

    async logout()
    {
       return await FirebaseAuth.signOut(this.mFirebaseAuth)
    }

    async resetPassword(email: string)
    {
        return FirebaseAuth.sendPasswordResetEmail(this.mFirebaseAuth, email)
    }

    async updateCurrentAccountPassword(oldPassword: string, newPassword: string)
    {
        if (!this.mFirebaseAuth.currentUser) {
            throw 'not logged in'
        }

        const email = this.mFirebaseAuth.currentUser.email
        if (!email) {
            throw 'invalid user email'
        }

        const emailCreds = FirebaseAuth.EmailAuthProvider.credential(email, oldPassword);
        let userCreds = await FirebaseAuth.reauthenticateWithCredential(this.mFirebaseAuth.currentUser, emailCreds)

        return FirebaseAuth.updatePassword(userCreds.user, newPassword);
    }

    async updateCurrentAccountProperties(props: any) {
        return this.mRemoteInterface.request("UPDATE_ACCOUNT_PROPERTIES", {props: props});
    }

    async updateCurrentAccountDimensions(records: any) {
        return this.mRemoteInterface.request("UPDATE_ACCOUNT_DIMENSIONS", {records: records});
    }

    async getAccountByEmail(email: string) {
        return this.mRemoteInterface.request("GET_ACCOUNT_BY_EMAIL", {email: email});
    }

    async getAccountsEmails(accounts: string[]) {
        return this.mRemoteInterface.request("GET_ACCOUNTS_EMAILS", {accounts: accounts});
    }

    async sendRequest(type: string, message: any): Promise<any> {
        return this.mRemoteInterface.request(type, message);
    }

}

const g_accountService = new AccountService();

export default g_accountService;
